import type React from "react";
import { memo, useCallback, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createDocumentDesign } from "../api/client";
import type { DocumentDesignState } from "../canvas/state/types";
import { env } from "../import-meta-env";
import { DuplicateDesignModal } from "./DuplicateDesignModal/DuplicateDesignModal";
import {
  type CanvasPaperConfig,
  PageDesigner,
} from "./PageDesigner/PageDesigner";

const DesignerRouteRaw: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const accountId = params.get("account") ?? env.VITE_ACCOUNT_ID;
  const appId = params.get("app") ?? env.VITE_APP_ID;
  const documentDesignId =
    params.get("document_design") ?? env.VITE_DOCUMENT_DESIGN_ID;
  const solutionPrimaryColor =
    params.get("solution_primary_color") ?? undefined;

  const [duplicateModal, setDuplicateModal] = useState({
    active: false,
    copying: null as {
      state: DocumentDesignState;
      canvasPaperConfig: CanvasPaperConfig;
      name: string;
    } | null,
  });

  const handleDuplicateDesign = useCallback(
    (
      state: DocumentDesignState,
      canvasPaperConfig: CanvasPaperConfig,
      name: string,
    ) => {
      setDuplicateModal({
        active: true,
        copying: {
          state,
          canvasPaperConfig,
          name,
        },
      });
    },
    [],
  );

  const handleCancelDuplicate = useCallback(() => {
    setDuplicateModal({ active: false, copying: null });
  }, []);

  const handleConfirmDuplicate = useCallback(
    (copyName: string): void => {
      if (!duplicateModal.copying) {
        return;
      }

      const { state, canvasPaperConfig } = duplicateModal.copying;

      createDocumentDesign(appId, copyName, state, canvasPaperConfig)
        .then((data) => {
          navigate(
            `/?account=${accountId}&app=${appId}&document_design=${data.id}&solution_primary_color=${solutionPrimaryColor}`,
          );
        })
        .catch(console.error);

      setDuplicateModal({ active: false, copying: null });
    },
    [accountId, appId, duplicateModal.copying, navigate, solutionPrimaryColor],
  );

  return (
    <>
      <PageDesigner
        appId={appId}
        documentDesignId={documentDesignId}
        solutionPrimaryColor={solutionPrimaryColor}
        onDuplicateDesign={handleDuplicateDesign}
      />

      <DuplicateDesignModal
        active={duplicateModal.active}
        name={`${duplicateModal.copying?.name} Copy`}
        onConfirm={handleConfirmDuplicate}
        onCancel={handleCancelDuplicate}
      />
    </>
  );
};

export const DesignerRoute = memo(DesignerRouteRaw);
